import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import throwInDevOutsideReact from 'Helpers/throwInDevOutsideReact';

export const DATA_PREFIX = 'data-';

const makeValue = (v) => `${DATA_PREFIX}${v}`;

const dataValue = {
  dataValue: makeValue('value'),
  dataIndex: makeValue('index'),
  dataElementSemantic: makeValue('element-semantic'), // This should match something in the design system. It should explain the semantic of a component.
  dataResourceId: makeValue('resource-id'), // The id of a resource
  dataInstanceId: makeValue('instance-id'), // The id of an instance of a resource
  dataResourceType: makeValue('resource-type'), // The id of a resource
  dataAccountId: makeValue('account-id'),
  dataAccountType: makeValue('account-type'),
  dataAccountSubType: makeValue('account-sub-type'),
  dataRealAssetType: makeValue('real-asset-type'),
  dataType: makeValue('type'),
  dataTitle: makeValue('title'),
  dataActive: makeValue('active'),
  dataFavorite: makeValue('favorite'),
  dataArchived: makeValue('archived'),
  dataSearchCount: makeValue('search-count'),
  dataAction: makeValue('action'),
  dataAttrName: makeValue('attr-name'),
  dataFirstName: makeValue('first-name'),
  dataLastName: makeValue('last-name'),
  dataName: makeValue('name'),
};

export const testAttr = dataValue;

const allowedValuesMap = Object.values(dataValue).reduce((acc, curr) => {
  acc[curr] = true;

  return acc;
}, {});

export class DataValueMap {
  constructor() {
    this._values = {};
  }

  static init(semantic) {
    const inst = new DataValueMap();

    inst.append(dataValue.dataElementSemantic, semantic);

    return inst;
  }

  static keyMap = dataValue;

  append(key, value) {
    if (value === undefined || value === null) {
      return this;
    }
    if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
      return throwInDevOutsideReact(`Invalid DataValueMap value type: ${typeof value}`) ?? this;
    }

    if (!allowedValuesMap[key]) {
      return throwInDevOutsideReact(`Must be in allowedValuesMap: ${key}`) ?? this;
    }

    // Must convert value to a string because it could be a number or boolean, which don't have a replaceAll method

    // Commenting out because we have values with `/`. Checked with Blake B first
    // const invalidChars = `${value}`.replaceAll(/[0-9A-Za-z \-_]/g, '');
    //
    // if (invalidChars.length > 0) {
    //   return throwInDev(`Must be kebab-case. Original:${value} Found invalid chars: ${invalidChars}`) ?? this;
    // }

    // Keep _values consistently filled with strings, even if an input is a number or boolean. After all, the attribute values in HTML will all be strings anyway
    this._values[key] = `${value}`;

    return this;
  }

  set(obj) {
    let self = this;
    Object.entries(obj).forEach(entry => {
      self = self.append(...entry);
    });
    return self;
  }

  build() {
    return this._values;
  }
}

export const dataValueMapPropType = PropTypes.instanceOf(DataValueMap);

/**
 * @example <div {...dataAttrs('institution-picker')} />
 * @example <div {...dataAttrs('institution-picker', resource)} />
 * @example
 *  <div
 *    {...dataAttrs('institution-picker', {
 *      [testAttr.dataIndex]: 0,
 *      [testAttr.dataValue]: 'chase',
 *    })}
 *  />
 * @example
 *  <div
 *    {...dataAttrs('institution-picker', resource {
 *      [testAttr.dataIndex]: 0,
 *    })}
 *  />
 * @param semantic how you'd describe the element you're putting this on
 * @param resourceOrOtherAttrs a facet-rest resource - can be null/undefined
 * @param otherAttrs keys should be from testAttr, e.g. testAttr.dataResourceId, and values can be whatever you want
 * @return {Record<string,string>}
 */
export const dataAttrs = (semantic, resourceOrOtherAttrs = undefined, otherAttrs = {}) => {
  let res = DataValueMap.init(semantic);
  // Will be in Object.entries form - like [[key, val], [key2, val2], ...]
  const attrsEntriesToAdd = [];
  if (resourceOrOtherAttrs instanceof fr.Resource) {
    attrsEntriesToAdd.push([testAttr.dataResourceId, resourceOrOtherAttrs.id()],
      [testAttr.dataResourceType, resourceOrOtherAttrs.type()],
      [testAttr.dataInstanceId, resourceOrOtherAttrs.instanceId()]);
  } else if (resourceOrOtherAttrs) {
    attrsEntriesToAdd.push(...Object.entries(resourceOrOtherAttrs));
  }

  if (otherAttrs) {
    attrsEntriesToAdd.push(...Object.entries(otherAttrs));
  }

  attrsEntriesToAdd.forEach(entry => {
    res = res.append(...entry);
  });
  return res.build();
};

export const useDataAttrs = (props) => {
  return React.useMemo(() => {
    return Object.entries(props).reduce((acc, [k, v]) => {
      if (k.startsWith('data-')) {
        acc[k] = v;
      }

      return acc;
    }, {});
  }, [props]);
};
